import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
/**
 * HERO app-window module — "Kopilot-KI" (design map §3; prototype `isKopilot`
 * block, Hausverwaltung Hero.dc.html lines 1538–1679).
 *
 * NOT chromeless: it keeps the shared app top-bar, but paints its OWN body via
 * the registry `customBody` flag (height 640, warm `#EFEEEA` ground) instead of
 * the standard 560/#fff sidebar body. Two layers: a floating white card
 * (Vorgang + Detail) over a Kopilot chat column on the warm ground.
 */
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  CornerDownLeft,
  EllipsisVertical,
  GitBranchPlus,
  History,
  Lightbulb,
  Paperclip,
  SquarePen,
  Wrench,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { HeroVorgangActionFooter } from "./hero-vorgang-action-footer";
import { HeroVorgangDetailRail } from "./hero-vorgang-detail-rail";
import type { HeroVorgangRailData } from "./hero-vorgang-detail-rail";
import type { HeroVorgangMessageData } from "./hero-vorgang-message";
import { HeroVorgangThread } from "./hero-vorgang-thread";
import type { HeroVorgangThreadItem } from "./module-inbox-vorgang-data";

function kopilotMessage(t: Translate): HeroVorgangMessageData {
  return {
    id: "kp-email-1",
    direction: "inbound",
    channel: "email",
    subject: t("aufzugHausAusserBetrieb"),
    fromLine: "Renate Brandt <r.brandt@email.de>",
    authorName: "Renate Brandt",
    authorInitials: "RB",
    authorAvatarBg: ld("#EAEFE9", "#2f3236"),
    authorAvatarFg: ld("#5E7C66", "#a5a9ae"),
    roleLabel: t("mieterinWe12"),
    timestamp: t("heute0842"),
    fullPlain: t("gutenTagAufzugUnserem"),
  };
}

function kopilotThread(t: Translate): readonly HeroVorgangThreadItem[] {
  return [{ kind: "message", message: kopilotMessage(t) }];
}

function kopilotRail(t: Translate): HeroVorgangRailData {
  return {
    externalId: "#2026-0481",
    title: t("aufzugHausAusserBetrieb"),
    statusLabel: "TO-DO",
    assignee: { initials: "NH", name: t("ninaHartmann"), bg: ld("#0B0F19", "#fafafa") },
    board: { name: t("schaeden"), icon: "alert" },
    recipient: null,
    property: { name: t("ahornweg5"), unit: "WE 12 · 4. OG" },
    openPickers: ["due", "priority", "tags"],
  };
}

const CHAT_ICON_BTN: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
  cursor: "pointer",
};

/** The floating white card: Vorgang (left) + Detail rail (right). */
function KopilotCard(): ReactNode {
  const t = useTranslations("marketing.moduleKopilot");
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        background: ld("#fff", "#181b1f"),
        borderRadius: 14,
        boxShadow:
          `0 26px 50px -26px ${ld("rgba(11,15,25,0.34)", "rgba(0,0,0,0.56)")}, 0 3px 10px -5px ${ld("rgba(11,15,25,0.12)", "rgba(0,0,0,0.23)")}`,
        overflow: "hidden",
        position: "relative",
        zIndex: 2,
      }}
    >
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* VORGANG */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            background: ld("#fff", "#181b1f"),
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              padding: "13px 18px",
              borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <ArrowLeft width={18} height={18} strokeWidth={1.8} />
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 15,
                fontWeight: 700,
                color: ld("#0B0F19", "#fafafa"),
                minWidth: 0,
              }}
            >
              <span
                style={{
                  color: ld("#B65C5C", "#eb8c8a"),
                  display: "inline-flex",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle width={16} height={16} strokeWidth={1.9} />
              </span>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("aufzugHausAusserBetrieb")}
              </span>
            </span>
            <span
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <EllipsisVertical width={18} height={18} strokeWidth={1.8} />
            </span>
          </div>

          <div
            style={{
              position: "relative",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <HeroVorgangThread items={kopilotThread(t)} />
            <HeroVorgangActionFooter />
          </div>
        </div>

        <HeroVorgangDetailRail rail={kopilotRail(t)} />
      </div>
    </div>
  );
}

/** The Kopilot chat column (background layer, right of the card). */
function KopilotChat(): ReactNode {
  const t = useTranslations("marketing.moduleKopilot");
  return (
    <div
      style={{
        width: 362,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: "relative",
        zIndex: 1,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 38,
          padding: "0 2px",
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            background: ld("#0B0F19", "#fafafa"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="10"
            height="8"
            style={{ display: "block" }}
            aria-hidden="true"
          >
            <path
              d="M5 1.2 L8.8 6.8 L1.2 6.8 Z"
              fill={ld("#fff", "#181b1f")}
              stroke={ld("#fff", "#181b1f")}
              strokeWidth={2.3}
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: ld("#0B0F19", "#fafafa") }}>
          {t("kopilot")}
        </span>
        <span style={{ fontSize: 11.5, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
          {t("kiAssistent")}
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <span style={CHAT_ICON_BTN}>
            <History width={16} height={16} strokeWidth={1.8} />
          </span>
          <span style={CHAT_ICON_BTN}>
            <SquarePen width={16} height={16} strokeWidth={1.8} />
          </span>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 11,
          padding: "2px 2px 0",
        }}
      >
        <div
          style={{
            alignSelf: "flex-end",
            maxWidth: "84%",
            background: ld("#E4E2DC", "#383b40"),
            borderRadius: "13px 13px 5px 13px",
            padding: "9px 13px",
            fontSize: 13,
            lineHeight: 1.5,
            color: ld("#2A2F3A", "#e0e4e9"),
          }}
        >
          {t("aufzugAhornweg5Ausgefallen")}
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.62, color: ld("#1C2230", "#e7ebf0") }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 11.5,
              color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
              marginBottom: 8,
            }}
          >
            <Wrench width={13} height={13} strokeWidth={1.8} />
            {t("vorschlagNebenkonversationErstellen")}
          </div>
          <div>
            {t("neinFuer")}{" "}
            <strong style={{ fontWeight: 600 }}>{t("ahornweg5")}</strong>{" "}
            {t("wurdeNichtsGemeldetZustaendig")}{" "}
            <strong style={{ fontWeight: 600 }}>
              {t("aufzugsdienstRheinlandGmbh")}
            </strong>
            {t("ichHabeBeauftragungVorbereitet")}
          </div>
        </div>

        <div
          style={{
            // Never squeezed by the column — else the "Als Entwurf öffnen" row gets clipped.
            flexShrink: 0,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E8EAED", "#323539")}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              background: ld("#F5F7FA", "#24272b"),
              borderBottom: `1px solid ${ld("#ECEFF2", "#2e3135")}`,
              fontSize: 12,
              color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
            }}
          >
            <span style={{ color: ld("#C0453E", "#ff7e73"), display: "inline-flex" }}>
              <GitBranchPlus width={14} height={14} strokeWidth={1.8} />
            </span>
            {t("nebenkonversationFuer")}{" "}
            <strong style={{ fontWeight: 700, color: ld("#0B0F19", "#fafafa") }}>
              {t("k20260481")}
            </strong>
          </div>
          <div style={{ padding: "11px 15px" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: ld("#0B0F19", "#fafafa") }}>
              {t("aufzugReparaturBeauftragen")}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                marginTop: 3,
              }}
            >
              {t("technikAufzugsdienstRheinlandDe")}
            </div>
            <div
              style={{ height: 1, background: ld("#EFEFF1", "#2d3034"), margin: "10px 0" }}
            />
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.62,
                color: ld("#2A2F3A", "#e0e4e9"),
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {t("sehrGeehrteDamenHerren")}
              <br />
              <br />
              {t("hiermitBeauftragenInstandsetzungAufzugsanlage")}
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${ld("#EFEFF1", "#2d3034")}`, padding: "9px 12px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                height: 36,
                border: `1px solid ${ld("#E5E6E3", "#35383d")}`,
                borderRadius: 9,
                fontSize: 12.5,
                color: ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
                cursor: "pointer",
              }}
            >
              <CornerDownLeft width={14} height={14} strokeWidth={1.8} />
              {t("entwurfOeffnen")}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          flexShrink: 0,
          marginTop: 11,
          background: ld("#fff", "#181b1f"),
          border: `1px solid ${ld("#E3E3DE", "#383b3f")}`,
          borderRadius: 13,
          padding: "10px 13px",
          boxShadow: `0 8px 22px -14px ${ld("rgba(11,15,25,0.22)", "rgba(0,0,0,0.38)")}`,
        }}
      >
        <div style={{ fontSize: 12.5, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
          {t("nachrichtSchreiben")}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            marginTop: 10,
          }}
        >
          <span
            style={{
              color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
              display: "inline-flex",
              cursor: "pointer",
            }}
          >
            <Paperclip width={16} height={16} strokeWidth={1.8} />
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 9px",
              border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
              borderRadius: 7,
              fontSize: 11.5,
              color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
            }}
          >
            <Lightbulb width={13} height={13} strokeWidth={1.8} />
            {t("vorschlag")}
            <ChevronDown width={12} height={12} strokeWidth={1.8} />
          </span>
          <span
            style={{
              marginLeft: "auto",
              fontFamily: H.label,
              fontSize: 11,
              color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
            }}
          >
            5.4
          </span>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: H.accent,
              color: ld("#fff", "#181b1f"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ArrowUp width={16} height={16} strokeWidth={2} />
          </span>
        </div>
      </div>
    </div>
  );
}

export function ModuleKopilot(_props: ModuleProps): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        height: 640,
        background: ld("#EFEEEA", "#2e3135"),
        padding: 14,
        gap: 18,
        boxSizing: "border-box",
      }}
    >
      <KopilotCard />
      <KopilotChat />
    </div>
  );
}
